// TS mirror of the Go chat domain (apps/base/domain/chat). The backend is the
// source of truth for the model and all authorization; the frontend reads these
// shapes and renders. AccessMode presets are duplicated here only so the UI can
// reason about capabilities (can I write? am I the owner?) without a round-trip.

type TopicType = 'p2p' | 'grp'

type EntityRef = {
	kind: string
	id: string
}

type Topic = {
	id: string
	type: TopicType
	name: string
	owner: string
	seqId: number
	touchedAt: string
	public?: Record<string, unknown>
	entity?: EntityRef
	createdAt: string
}

// A topic paired with the viewer's unread count, the shape the conversation list
// consumes.
type TopicView = Topic & {
	unread: number
	peerName?: string
}

type Message = {
	id: string
	topic: string
	seqId: number
	from: string
	fromName: string
	head?: Record<string, unknown>
	content: Record<string, unknown>
	createdAt: string
	deletedAt?: string
}

// AccessMode bit-field, mirroring the Go domain. Only the presets the UI needs
// are spelled out; the raw bits live on the subscription if finer checks arise.
const ModeJoin = 1 << 0
const ModeRead = 1 << 1
const ModeWrite = 1 << 2
const ModeApprove = 1 << 4
const ModeShare = 1 << 5
const ModeOwner = 1 << 7

const hasMode = (mode: number, want: number) => (mode & want) === want

// Minimal member read model for the conversation member picker. Kept local to
// avoid coupling to the HR/members feature; resolved via the chat backend's own
// member lookup, mirroring the workflows isolation pattern.
type ChatMember = {
	id: string
	name: string
	avatarUrl?: string
}

// The message body the composer sends. Text-only for now; head/content stay open
// maps so richer payloads (attachments, replies) can be added without a model
// change.
type ReplyInfo = {
	id: string
	name: string
	text: string
}

const getReplyInfo = (head: Record<string, unknown> | undefined): ReplyInfo | null => {
	if (!head?.replyTo || typeof head.replyTo !== 'string') return null
	return {
		id: head.replyTo,
		name: typeof head.replyToName === 'string' ? head.replyToName : '',
		text: typeof head.replyToTxt === 'string' ? head.replyToTxt : '',
	}
}

type SendMessageInput = {
	topicId: string
	content: Record<string, unknown>
	head?: Record<string, unknown>
}

type ChatPort = {
	listTopics(): Promise<TopicView[]>
	listMessages(topicId: string, before: number, limit: number): Promise<Message[]>
	startP2P(peerId: string): Promise<Topic>
	createGroup(name: string, memberIds: string[], publicMeta?: Record<string, unknown>): Promise<Topic>
	getOrCreateEntityTopic(kind: string, id: string): Promise<Topic>
	publish(input: SendMessageInput): Promise<Message>
	markRead(topicId: string, seqId: number): Promise<void>
	addMember(topicId: string, userId: string): Promise<void>
	removeMember(topicId: string, userId: string): Promise<void>
	deleteMessage(messageId: string, scope: 'all' | 'self'): Promise<void>
	listSubscriptions(topicId: string): Promise<ChatMember[]>
	listMembers(query: string | null): Promise<ChatMember[]>
}

export { ModeJoin, ModeRead, ModeWrite, ModeApprove, ModeShare, ModeOwner, hasMode }
export type { TopicType, EntityRef, Topic, TopicView, Message, ChatMember, SendMessageInput, ChatPort, ReplyInfo }
export { getReplyInfo }
